import React, { useState } from "react";
import { Modal, Form, notification, Upload, Row, Col, Input } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Joi from "joi";
import axios from "axios";
import config from "../../../../config/config";
import helper from "../../../../helpers/helper";

const Create = ({ isVisible, handleClose, moduleName, base_url }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});

  const sizeLimit = config.sizeLimit;
  const allowedExtensions = config.allowed_extensions;
  const joiOptions = config.joiOptions;

  // ------------------- VALIDATION -------------------
  const validate = (value) => {
    const schemaObj = {
      mainImg: Joi.required().label("Desktop Image"),
      mbMainImg: Joi.required().label("Mobile Image"),
      thumb: Joi.string().required().label("Image Alt Text"),
    };

    const schema = Joi.object(schemaObj).options({ allowUnknown: true });
    const { error } = schema.validate(value, joiOptions);

    const errors = {};
    if (error) {
      error.details.forEach((field) => {
        errors[field.path[0]] = field.message;
      });
    }

    // FIXED: extract files without mutating value
    const mainImgFile = value.mainImg?.[0]?.originFileObj;
    const mbMainImgFile = value.mbMainImg?.[0]?.originFileObj;

    if (mainImgFile) {
      helper.validateSize("mainImg", mainImgFile, sizeLimit, errors);
      helper.validateExt("mainImg", mainImgFile, allowedExtensions, errors);
    }
    if (mbMainImgFile) {
      helper.validateSize("mbMainImg", mbMainImgFile, sizeLimit, errors);
      helper.validateExt("mbMainImg", mbMainImgFile, allowedExtensions, errors);
    }

    return errors;
  };

  // ------------------- SUBMIT -------------------
  const handleSubmit = async (values) => {
    const errors = validate(values);
    setFormError(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);

    const formData = new FormData();
    const itemsData = [
      {
        mainImg: "temp",
        mbMainImg: "temp",
        thumb: values.thumb,
      },
    ];

    // This works reliably when showPreviewIcon: false
    const mainFile = values.mainImg?.file;
    const mbFile = values.mbMainImg?.file;

    if (!mainFile || !mbFile) {
      notification.error({
        message: "Please upload both Desktop and Mobile images",
        placement: "topRight",
      });
      setLoading(false);
      return;
    }

    formData.append("items", JSON.stringify(itemsData));
    formData.append("mainImg_0", mainFile);
    formData.append("mbMainImg_0", mbFile);

    try {
      const response = await axios.post(`${base_url}addHosLife`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.data?.message) {
        notification.success({
          message: `${moduleName} Added Successfully`,
          placement: "topRight",
        });
        form.resetFields();
        handleClose(true);
      }
    } catch (error) {
      notification.error({
        message: error?.response?.data?.message || "Something went wrong",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    beforeUpload: () => false,
    maxCount: 1,
    listType: "picture-card",
    accept: ".jpg,.jpeg,.png",
    showUploadList: {
      showPreviewIcon: false,
      showRemoveIcon: true,
    },
  };

  return (
    <Modal
      title={`Add ${moduleName}`}
      open={isVisible}
      onCancel={() => handleClose(false)}
      okText="Create"
      cancelText="Cancel"
      confirmLoading={loading}
      width={700}
      onOk={() => {
        form
          .validateFields()
          .then((values) => handleSubmit(values))
          .catch(() => {});
      }}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Desktop Image"
              extra="Recommended size: 2310 × 1031"
              name="mainImg"
              required
              {...(formError.mainImg && {
                help: formError.mainImg,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <PlusOutlined />
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              label="Mobile Image"
              extra="Recommended size: 750 × 1374"
              name="mbMainImg"
              required
              {...(formError.mbMainImg && {
                help: formError.mbMainImg,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <PlusOutlined />
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              required
              label="Image Alt Text"
              name="thumb"
              {...(formError.thumb && {
                help: formError.thumb,
                validateStatus: "error",
              })}
            >
              <Input placeholder="Enter alternative text for image" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Create;
